<template>
  <div class="d-inline-flex align-center mr-4 my-2" data-test-id="page-diff">
    <template v-for="(side, i) in sides" :key="side.name">
      <span v-if="i > 0" class="mx-2">→</span>
      <div class="page" :data-test-id="`page-${side.name}`">
        <div v-if="tagsOf(side.page).length" class="page_bp_tag-container"><TagDot v-for="tag in tagsOf(side.page)" :key="tag._id" :color="tag.color" size="8" /></div>
        <PageThumbnail v-if="side.page.thumbnail?.key" :thumbnail="side.page.thumbnail" :clickable="false" />
        <PageSvg v-else :fragments="side.page.fragments || []" :categories="categories" :textOf="textOf" />
      </div>
    </template>
  </div>
</template>

<script>
import { computed } from "vue"
import PageSvg from "@/modules/page/components/PageSvg"
import PageThumbnail from "@/modules/page/components/PageThumbnail"
import TagDot from "@/modules/tag/components/TagDot"
import { sameJson } from "@/utils/general"
import { frozenText } from "../describe"

// what a side shows
const DRAWN = ["template", "fragments", "tagRefs", "thumbnail"]

// A page as an activity row stored it, before and after a change (either side null). Its text,
// category colours and tags are the ones frozen in the row's labels. A side with an image shows it,
// as the flatplan does: data stores an edit's image only when the edit changed it. An edit that changed
// nothing drawn, such as the notes or the lock, draws the page once.
export default {
  name: "PageDiff",
  props: {
    before: {
      type: Object,
      default: null,
    },
    after: {
      type: Object,
      default: null,
    },
    labels: {
      type: Object,
      required: true,
    },
  },
  components: {
    PageSvg,
    PageThumbnail,
    TagDot,
  },
  setup(props) {
    // a computed: a live refresh can extend an entry's drawing with a newer row's labels
    let categories = computed(() =>
      Object.entries(props.labels)
        .filter(([, label]) => label.color)
        .map(([_id, { color }]) => ({ _id, color }))
    )

    // the text frozen in the row rather than today's content
    const textOf = (fragment) => frozenText(fragment, props.labels)

    let same = computed(() => !!props.before && !!props.after && DRAWN.every((field) => sameJson(props.before[field], props.after[field])))

    // the after side alone when nothing drawn changed
    let sides = computed(() => [props.before && !same.value && { name: "before", page: props.before }, props.after && { name: "after", page: props.after }].filter(Boolean))

    // tags already deleted when the row was written have no label
    const tagsOf = (page) => (page.tagRefs || []).filter((id) => props.labels[id]).map((id) => ({ _id: id, color: props.labels[id].color }))

    return {
      categories,
      textOf,
      sides,
      tagsOf,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "@/styles/page.scss";

.page {
  position: relative; // holds the tag dots, which sit below it as on the flatplan
}
</style>
